import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingBag, Palette, Megaphone, Globe, BookOpen, MessageCircle } from 'lucide-react';

interface Step {
  icon: React.ReactNode;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  link?: string;
}

export function HelpTab() {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();

  // Payments are handled automatically by the platform and shipping is only
  // relevant for stores with physical products, so neither belongs in the
  // general getting-started sequence. See the dedicated pages for details.
  const steps: Step[] = [
    {
      icon: <ShoppingBag className="h-5 w-5" />,
      titleKey: 'settingsHelp.addProducts',
      titleFallback: 'Add Your Products',
      descKey: 'settingsHelp.addProductsDesc',
      descFallback: 'Start by adding your products with images, prices, and descriptions. You can also organize them into categories.',
      link: `/${storeId}/products`,
    },
    {
      icon: <Palette className="h-5 w-5" />,
      titleKey: 'settingsHelp.customizeDesign',
      titleFallback: 'Customize Your Store Design',
      descKey: 'settingsHelp.customizeDesignDesc',
      descFallback: 'Personalize your store theme, colors, navigation, and pages to match your brand.',
      link: `/${storeId}/design`,
    },
    {
      icon: <Globe className="h-5 w-5" />,
      titleKey: 'settingsHelp.addDomain',
      titleFallback: 'Add a Custom Domain',
      descKey: 'settingsHelp.addDomainDesc',
      descFallback: 'Connect your own domain name to give your store a professional web address.',
      link: `/${storeId}/settings`,
    },
    {
      icon: <Megaphone className="h-5 w-5" />,
      titleKey: 'settingsHelp.startMarketing',
      titleFallback: 'Start Marketing',
      descKey: 'settingsHelp.startMarketingDesc',
      descFallback: 'Use coupons, analytical links, and social media to drive traffic and grow your customer base.',
      link: `/${storeId}/marketing`,
    },
  ];

  const resources: Array<{ icon: React.ReactNode; titleKey: string; titleFallback: string; descKey: string; descFallback: string }> = [
    {
      icon: <BookOpen className="h-5 w-5" />,
      titleKey: 'settingsHelp.documentation',
      titleFallback: 'Documentation',
      descKey: 'settingsHelp.documentationDesc',
      descFallback: 'Read our guides and tutorials to learn how to use all features of your dashboard.',
    },
    {
      icon: <MessageCircle className="h-5 w-5" />,
      titleKey: 'settingsHelp.contactSupport',
      titleFallback: 'Contact Support',
      descKey: 'settingsHelp.contactSupportDesc',
      descFallback: 'Need help? Reach out to our support team via the contact inbox or email.',
    },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Getting Started */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-1">{t('settingsHelp.gettingStarted', 'Getting Started')}</h2>
        <p className="text-sm text-text-muted mb-5">
          {t('settingsHelp.gettingStartedDesc', 'Follow these steps to set up your store and start selling.')}
        </p>
        <div className="space-y-3">
          {steps.map((step, i) => (
            <button
              key={i}
              type="button"
              onClick={() => step.link && navigate(step.link)}
              className="flex w-full items-start gap-4 rounded-lg border border-border-default bg-bg-primary p-4 text-start transition-colors hover:border-accent/40 hover:bg-bg-hover"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                {step.icon}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">{t(step.titleKey, step.titleFallback)}</p>
                <p className="mt-0.5 text-xs text-text-muted">{t(step.descKey, step.descFallback)}</p>
              </div>
              <span className="ms-auto shrink-0 text-xs font-medium text-accent">{i + 1}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Resources */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-4">{t('settingsHelp.resources', 'Resources')}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {resources.map((res, i) => (
            <div key={i} className="rounded-lg border border-border-default bg-bg-primary p-4">
              <div className="flex items-center gap-2 mb-2 text-accent">
                {res.icon}
                <p className="text-sm font-medium">{t(res.titleKey, res.titleFallback)}</p>
              </div>
              <p className="text-xs text-text-muted">{t(res.descKey, res.descFallback)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
